import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useApiClient } from '@travel/api-client';
import { getCurrency, getMarket } from '@travel/i18n';
import { Info } from '@travel/icons/ui';
import { APPLICATION_TYPE } from '@travel/traveler-core/constants/salesPromotion';
import {
  useCurrencyChangeCallback,
  useDialogHandler,
  useGetTranslation,
} from '@travel/traveler-core/hooks';
import { CouponCover, CouponDetail, FlatButton } from '@travel/ui';

import L10nDateTime from 'components/Date';
import ErrorMessage from 'components/ErrorMessage';
import IncentiveValueTitle from 'components/IncentiveValueTitle';
import MessageBox from 'components/MessageBox';
import WithSkeletonLoading from 'components/WithSkeletonLoading';
import SearchFormDialog from 'pages/CouponListPage/components/SearchFormDialog';

import { getRouter } from 'store/__router/selectors';
import { linkNewCouponItem } from 'store/acquiredCoupons/api';
import { getMember } from 'store/member/selectors';
import { fetchCouponDetails } from 'store/salesPromotion/actions';
import { getCouponDetails, getIsCouponDetailFetching } from 'store/salesPromotion/selectors';

import { EXPIRED_REASON_LABELS } from 'constants/salesPromotion';
import { Translate } from 'core/translate';
import { useCouponDetailLabels } from 'hooks/useCouponLabels';
import useLogin from 'hooks/useLogin';
import usePageLoaderOnInitial from 'hooks/usePageLoaderOnInitial';
import { shouldDisplayCouponDisclaimer } from 'utils/salesPromotion';

import DataLayer from '../../components/DataLayer';

import styles from './couponDetailPage.module.scss';

type Props = {
  params: {
    couponId: string;
  };
};

function CouponDetailPage(props: Props) {
  const apiClient = useApiClient();
  const dispatch = useDispatch();
  const currency = useSelector(getCurrency);
  const market = useSelector(getMarket);
  const isCouponDetailFetching = useSelector(getIsCouponDetailFetching);
  const coupon = useSelector(getCouponDetails(props.params.couponId));
  const [linkingError, setLinkingError] = useState(false);

  const getTranslation = useGetTranslation();
  const couponDetailLabels = useCouponDetailLabels(getTranslation, currency, coupon?.localCurrency);

  const { isMember } = useSelector(getMember);
  const router = useSelector(getRouter);
  const {
    location: { location },
    baseUrl,
  } = router;

  const [isLoading, setIsLoading] = useState(false);

  const {
    isOpen: isSearchDialogOpen,
    onOpen: onSearchDialogOpen,
    onClose: onSearchDialogClose,
  } = useDialogHandler(false);

  const { onClickLogin: login } = useLogin();

  const isApplicableMarket =
    !coupon?.applicableMarkets?.length || coupon?.applicableMarkets.includes(market.marketCode);
  const isMobileAppOnly = coupon?.conditions.applicationTypes?.includes(
    APPLICATION_TYPE.MOBILE_APP,
  );
  const isShowLogin = coupon?.conditions.rakutenMemberRequired && !isMember;

  const linkCoupon = useCallback(async () => {
    setIsLoading(true);

    try {
      await linkNewCouponItem(apiClient, props.params.couponId, false);
      dispatch(fetchCouponDetails(props.params.couponId, true));
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setLinkingError(true);
    }
  }, [apiClient, dispatch, props.params.couponId]);

  const onClickLogin = useCallback(() => {
    login(baseUrl + location.pathname + location.search);
  }, [baseUrl, location.pathname, location.search, login]);

  const onCurrencyChange = useCallback(
    () => dispatch(fetchCouponDetails(props.params.couponId, true)),
    [dispatch, props.params.couponId],
  );

  useCurrencyChangeCallback(onCurrencyChange);

  const actionButton = (
    <div className={styles.actionWrapper}>
      <FlatButton
        className={styles.actionButton}
        type="button"
        isDisabled={coupon?.isExpired}
        onClick={isShowLogin ? onClickLogin : coupon?.isAcquired ? onSearchDialogOpen : linkCoupon}
        isLoading={isLoading}
        data-testid={`couponDetailPage-${
          isShowLogin ? 'login' : coupon?.isAcquired ? 'search' : 'link'
        }-button`}
      >
        <Translate
          id={
            isShowLogin
              ? 'Coupon_Details.Coupon.Navigation.Signup_Login'
              : coupon?.isAcquired
                ? 'Coupon_Details.Coupon.Navigation.Search_Form'
                : 'Coupon_Details.Coupon.Navigation.Get_Coupon'
          }
        />
      </FlatButton>
    </div>
  );

  usePageLoaderOnInitial('coupon_list', isCouponDetailFetching);

  return (
    <div data-testid="couponDetailPage-wrapper" className={styles.wrapper}>
      <WithSkeletonLoading
        isLoading={isCouponDetailFetching}
        count={3}
        row={3}
        length="long"
        customStyle="textWithImageRow"
        className={styles.loaderWrapper}
      >
        {((coupon?.isExpired && coupon?.expirationReason) || linkingError) && (
          <ErrorMessage
            className={styles.errorMessage}
            message={
              <Translate
                id={
                  coupon?.isExpired && coupon?.expirationReason
                    ? EXPIRED_REASON_LABELS[coupon.expirationReason]
                    : 'Coupon_List.Title.Coupon_Type.Feedback_Message'
                }
              />
            }
          />
        )}
        <CouponCover
          className={styles.couponCover}
          incentiveAmount={coupon?.incentiveAmount}
          promotionEnd={coupon?.promotionEnd}
          promotionStart={coupon?.promotionStart}
          title={coupon?.title}
          currency={currency}
          localCurrency={coupon?.localCurrency}
          isDisplayPeriod={true}
          DateComponent={L10nDateTime}
          CouponTitleComponent={({ incentiveAmount, currency, localCurrency }) => (
            <IncentiveValueTitle
              {...incentiveAmount}
              currency={currency}
              localCurrency={localCurrency}
            />
          )}
        />
        {shouldDisplayCouponDisclaimer(coupon, currency) && (
          <Translate
            className={styles.annotation}
            id="Coupon_Details.Price_Disclaimer"
            condition={() => `status == different_currency`}
          />
        )}
        {isShowLogin ? (
          <>
            <MessageBox
              className={styles.message}
              data-testid="couponDetail-messageBox-login"
              icon={<Info size={16} className={styles.infoIcon} />}
              message={<Translate id="Coupon_Details.User_Login.Status" />}
            />
            {actionButton}
          </>
        ) : isApplicableMarket && !isMobileAppOnly ? (
          <>{actionButton}</>
        ) : (
          <>
            {isMobileAppOnly && (
              <MessageBox
                className={styles.message}
                data-testid="couponDetail-messageBox-mobileAppOnly"
                icon={<Info size={16} className={styles.infoIcon} />}
                message={<Translate id="Traveler_Common.Promotion.Platform.App_Only_Error" />}
              />
            )}
            {!isApplicableMarket && (
              <MessageBox
                className={styles.message}
                data-testid="couponDetail-messageBox-market"
                icon={<Info size={16} className={styles.infoIcon} />}
                message={<Translate id="Coupon_Details.Applicable_User_Region.Not_Match_Coupon" />}
              />
            )}
          </>
        )}

        {coupon && (
          <CouponDetail
            className={styles.couponDetail}
            couponDetail={coupon}
            currency={currency}
            labels={couponDetailLabels}
            customHeaderTitle={<Translate id="Coupon_Details.Conditions.Title" />}
          />
        )}

        {/* Dialog */}
        {isSearchDialogOpen && coupon && (
          <SearchFormDialog
            couponId={props.params.couponId}
            coupon={coupon}
            isCouponExpired={coupon.isExpired}
            isOpen={isSearchDialogOpen}
            onClose={onSearchDialogClose}
          />
        )}
      </WithSkeletonLoading>
      <DataLayer
        pageName="account_coupons:coupons_detail"
        pageType="top"
        siteSection="account_coupons"
      />
    </div>
  );
}

export default CouponDetailPage;
